// Which catalog items read which names. Used to answer "applies to" for a standard.

import { identifiers, type LibCatalogItem, type Library } from '@zerogap/engine';

function names(expr: string | null | undefined, into: Set<string>) {
  if (!expr) return;
  try {
    identifiers(expr).forEach((n) => into.add(n));
  } catch {
    /* a broken formula is reported where it is evaluated */
  }
}

export function namesRead(cat: LibCatalogItem): Set<string> {
  const set = new Set<string>();
  cat.derived.forEach((d) => names(d.expr, set));
  cat.inputs.forEach((i) => {
    names(i.rule_expr, set);
    names(i.hidden_expr, set);
    // an input that follows a standard reads it
    if (i.data_type === 'standard') set.add(i.standard_key?.trim() || i.key);
  });
  if (cat.recipe_standard?.trim()) set.add(cat.recipe_standard.trim());
  cat.recipes.forEach((r) => {
    names(r.unavailable_expr, set);
    r.route.forEach((s) => names(s.qty_expr, set));
    r.bom.forEach((b) => {
      names(b.qty_expr, set);
      names(b.unit_price_expr, set);
      if (b.material_role) set.add(b.material_role);
    });
    r.parts.forEach((p) => {
      [p.qty_expr, p.length_expr, p.width_expr, p.band_long_expr, p.band_short_expr].forEach((e) => names(e, set));
      if (p.material_role) set.add(p.material_role);
      // thickness_<role> and sheet_area_<role> read the material filling that role
      [...set].forEach((n) => {
        const m = /^(?:thickness|sheet_area)_(.+)$/.exec(n);
        if (m) set.add(m[1]!);
      });
    });
  });
  cat.children.forEach((c) => {
    names(c.count_expr, set);
    Object.values(c.input_map).forEach((e) => names(e, set));
  });
  return set;
}

/** The catalog items a standard applies to: the ones whose formulas read it. */
export function appliesTo(lib: Library, key: string): string[] {
  const out: string[] = [];
  for (const cat of Object.values(lib.catalog)) {
    const read = namesRead(cat);
    // a choice standard also applies where one of the material roles it fills is used
    const def = lib.standards.find((d) => d.key === key);
    const roles = new Set<string>();
    def?.choices?.forEach((c) => Object.keys(c.materials ?? {}).forEach((r) => roles.add(r)));
    if (read.has(key) || [...roles].some((r) => read.has(r))) out.push(cat.name);
  }
  return out.sort();
}
